import type {
  IUpdateClientFlagsBody,
  IUpdateClientMetadataBody,
} from "@gen/app-query";
import {
  CoreQuery,
  UpdateClientFlagsBody,
  UpdateClientMetadataBody,
} from "@gen/app-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ClearableSelectWithPlaceholder } from "~/components/select_with_placeholder";
import { TextInput } from "~/components/text_input";
import { useConfig } from "~/contexts/config";
import { moneyInputPattern } from "~/entities/money";
import { DaysHoursFields } from "~/features/client/DaysHoursFields";
import { merchantCategoryCodes } from "~/services/mcc";
import { formatMoneyInput, Money, parseMoney } from "~/utilities/money";
import { config } from "~root/config";

// `debt_threshold_major` holds the refund overdraft limit as entered by the
// user, in the major units of the default currency of the client's connected
// account. It's converted to and from `debt_threshold`, which is in the
// smallest currency unit.
type ClientMetadataFormValues = IUpdateClientMetadataBody &
  IUpdateClientFlagsBody & {
    debt_threshold_major: string;
  };

export function ClientMetadataEditor({
  clientId,
  canEdit = true,
}: {
  clientId: string;
  canEdit?: boolean;
}) {
  const { data: clientData, isFetching: isClientFetching } =
    CoreQuery.useAdmin_getClientQuery(clientId);

  const { data: clientFlagsData, isFetching: isClientFlagsFetching } =
    CoreQuery.useAdmin_getClientFlagsQuery(clientId);

  const { data: feesData } = CoreQuery.useAdmin_getClientFeesQuery(clientId);

  const hasConnectedAccount = Boolean(
    clientData?.on_behalf_of_stripe_connected_account_id,
  );
  const {
    data: connectedAccountData,
    isFetching: isConnectedAccountFetching,
    isError: isConnectedAccountError,
  } = CoreQuery.useAdmin_getClientConnectedAccountQuery(clientId, {
    enabled: hasConnectedAccount,
  });
  const debtThresholdCurrency = hasConnectedAccount
    ? connectedAccountData?.default_currency?.toUpperCase()
    : undefined;

  const { clientMetadata: clientMetadataConfig } = useConfig();

  const locale = navigator.languages?.[0] || config.localization.locale;

  const queryClient = useQueryClient();

  const { mutate: updateMetadata, isPending: isMetadataUpdatePending } =
    CoreQuery.useAdmin_updateClientMetadataMutation(clientId, {
      meta: { success: "Client metadata updated" },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientQueryKey(clientId),
        });
        // The on behalf of account may have changed, which changes the account
        // status shown in the account status banner.
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientConnectedAccountQueryKey(clientId),
        });
      },
    });

  const { mutate: updateFlags, isPending: isFlagsUpdatePending } =
    CoreQuery.useAdmin_updateClientFlagsMutation(clientId, {
      meta: { success: "Client metadata updated" },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientFlagsQueryKey(clientId),
        });
      },
    });

  const memoValues = useMemo<ClientMetadataFormValues>(
    () => ({
      stripe_platform_id: clientData?.stripe_platform_id ?? "",
      on_behalf_of_stripe_connected_account_id:
        clientData?.on_behalf_of_stripe_connected_account_id ?? "",
      integration_type: clientData?.integration_type ?? "",
      notes: clientData?.notes ?? "",
      postmark_prefix: clientData?.postmark_prefix ?? "",

      emails_disabled: clientData?.emails_disabled ?? false,
      payment_reviews_enabled: clientData?.payment_reviews_enabled ?? false,
      claiming_enabled: clientData?.claiming_enabled ?? false,
      is_internal: clientData?.is_internal ?? false,
      payment_capture_enabled: clientData?.payment_capture_enabled ?? false,
      consolidated_payouts_enabled:
        clientData?.consolidated_payouts_enabled ?? false,
      instant_payouts_enabled: clientData?.instant_payouts_enabled ?? false,
      future_usage_payment_enabled:
        clientData?.future_usage_payment_enabled ?? false,
      show_account_balance: clientFlagsData?.show_account_balance ?? false,
      automatic_handover_confirmation_duration_seconds:
        clientData?.automatic_handover_confirmation_duration_seconds,
      accept_deposit_allowance_seconds:
        clientData?.accept_deposit_allowance_seconds,
      automatic_cancellation_enabled:
        clientData?.automatic_cancellation_enabled ?? true,
      cover_extra_processing_cost:
        clientData?.cover_extra_processing_cost ?? false,
      allocate_extra_price_to_client:
        clientData?.allocate_extra_price_to_client ?? false,
      claim_email_matching_enabled:
        clientData?.claim_email_matching_enabled ?? false,
      cover_postage_price_processing_cost:
        clientData?.cover_postage_price_processing_cost ?? false,
      allocate_postage_price_to:
        clientData?.allocate_postage_price_to ?? "seller",
      price_postage_bearer: clientData?.price_postage_bearer ?? "buyer",
      mcc: clientData?.mcc ?? "",
      product_description: clientData?.product_description ?? "",
      website_url: clientData?.website_url ?? "",
      refund_after_funds_released_enabled:
        clientData?.refund_after_funds_released_enabled ?? false,
      debt_threshold: clientData?.debt_threshold,
      debt_threshold_major:
        clientData?.debt_threshold !== undefined && debtThresholdCurrency
          ? formatMoneyInput(
              clientData.debt_threshold,
              debtThresholdCurrency,
              locale,
            )
          : "",
      tax_reporting_eligible: clientData?.tax_reporting_eligible ?? false,
      tax_report_type: clientData?.tax_report_type ?? "dac7",
      supplied_platform_operator_id:
        clientData?.supplied_platform_operator_id ?? "",
    }),
    [clientData, clientFlagsData, debtThresholdCurrency, locale],
  );

  const mccOptions = useMemo(() => {
    return merchantCategoryCodes.map((category) => ({
      value: category.code,
      label: `${category.description} - ${category.code}`,
    }));
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<ClientMetadataFormValues>({
    defaultValues: memoValues,
  });

  useEffect(() => {
    reset(memoValues);
  }, [memoValues, reset]);

  const isRefundAfterFundsReleasedEnabled = useWatch({
    control,
    name: "refund_after_funds_released_enabled",
  });

  const isTaxReportingEligible = useWatch({
    control,
    name: "tax_reporting_eligible",
  });

  const isConsolidatedPayoutsEnabled = useWatch({
    control,
    name: "consolidated_payouts_enabled",
  });

  const isInstantPayoutsEnabled = useWatch({
    control,
    name: "instant_payouts_enabled",
  });

  const renderText = (
    name: keyof ClientMetadataFormValues,
    label: string,
    placeholder: string,
    options?: {
      rows?: number;
    },
  ) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => (
        <TextInput
          {...field}
          placeholder={placeholder}
          label={label}
          multiline={!!options?.rows}
          rows={options?.rows}
          clearable={canEdit}
          disabled={!canEdit}
          onClear={() => field.onChange("")}
          data-testid={`metadata-${name}-input`}
        />
      )}
    />
  );

  const renderDebtThreshold = () => {
    const isFieldDisabled =
      !canEdit || !isRefundAfterFundsReleasedEnabled || !debtThresholdCurrency;

    let warning: string | undefined;
    if (!hasConnectedAccount) {
      warning =
        "An on behalf of Stripe connected account must be set and saved for this client before a refund overdraft limit can be set, as the limit is in the default currency of that account.";
    } else if (isConnectedAccountError) {
      warning =
        "Couldn't retrieve the on behalf of Stripe connected account of this client, so the currency of the refund overdraft limit is unknown.";
    }

    return (
      <Stack gap={1}>
        <Controller
          name="debt_threshold_major"
          control={control}
          rules={{
            validate: (value) =>
              value === "" ||
              moneyInputPattern.test(value) ||
              "Invalid amount format",
          }}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              placeholder="Enter a Threshold"
              label={
                <Stack flexDirection="row" alignItems="center">
                  Refund Overdraft Limit
                  {debtThresholdCurrency && ` (${debtThresholdCurrency})`}
                  &nbsp;
                  <Tooltip
                    title={
                      <>
                        Set the maximum total sum of refunds costs the client
                        can accrue to Trustap, in the default currency of the
                        client&apos;s on behalf of Stripe connected account.
                        Only applies to post-release refunds. Any attempt to
                        process a post-release refund that would result in
                        exceeding this limit will be blocked.
                      </>
                    }
                  >
                    <PublicSvgIcon name="info" />
                  </Tooltip>
                </Stack>
              }
              clearable={canEdit}
              onClear={() => field.onChange("")}
              disabled={isFieldDisabled}
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  startAdornment: debtThresholdCurrency && (
                    <InputAdornment position="start">
                      {Money.getCurrencyInfo(debtThresholdCurrency).symbol ??
                        debtThresholdCurrency}
                    </InputAdornment>
                  ),
                },
              }}
              data-testid="metadata-debt_threshold-input"
            />
          )}
        />
        {warning && (
          <Alert
            severity="warning"
            data-testid="metadata-debt_threshold-warning"
          >
            {warning}
          </Alert>
        )}
      </Stack>
    );
  };

  const renderSuppliedPlatformOperatorID = () => (
    <Controller
      name="supplied_platform_operator_id"
      control={control}
      rules={{
        validate: (value) =>
          !getValues("tax_reporting_eligible") ||
          !!value ||
          "A Supplied Platform Operator ID is required to enable tax compliance tracking",
      }}
      render={({ field, fieldState }) => (
        <TextInput
          {...field}
          placeholder="Enter a Platform Operator ID"
          label="Supplied Platform Operator ID"
          clearable={canEdit}
          onClear={() => field.onChange("")}
          disabled={!canEdit}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          data-testid="metadata-supplied_platform_operator_id-input"
        />
      )}
    />
  );

  const renderToggle = (
    name: keyof ClientMetadataFormValues,
    label: string,
    options?: {
      tooltipTitle?: ReactNode;
      onChange?: (isChecked: boolean) => void;
    },
  ) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => (
        <Stack alignItems="center" gap={1} flexDirection="row">
          <Switch
            checked={!!field.value}
            disabled={!canEdit}
            onChange={(e) => {
              field.onChange(e.target.checked);
              options?.onChange?.(e.target.checked);
            }}
            slotProps={{
              input: {
                "data-testid": `metadata-${name}-switch`,
              },
            }}
          />
          <Typography variant="body2" lineHeight={1}>
            {label}&nbsp;
            {options?.tooltipTitle && (
              <Tooltip title={options.tooltipTitle}>
                <PublicSvgIcon
                  name="info"
                  sx={{
                    verticalAlign: "bottom",
                  }}
                />
              </Tooltip>
            )}
          </Typography>
        </Stack>
      )}
    />
  );

  const renderSelect = (
    name: keyof ClientMetadataFormValues,
    label: string,
    placeholder: string,
    options: { value: string; label: string }[],
    renderOptions?: {
      size?: "small" | "medium";
      width?: number;
      disabled?: boolean;
      isClearable?: boolean;
    },
  ) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          fullWidth={!renderOptions?.width}
          size={renderOptions?.size}
          sx={renderOptions?.width ? { width: renderOptions.width } : undefined}
        >
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <ClearableSelectWithPlaceholder
            labelId={`${name}-label`}
            label={label}
            placeholder={placeholder}
            {...field}
            value={field.value ?? ""}
            disabled={!canEdit || renderOptions?.disabled}
            isClearable={
              canEdit &&
              !renderOptions?.disabled &&
              (renderOptions?.isClearable ?? true)
            }
            onChange={(e) => field.onChange(e.target.value)}
            data-testid={`metadata-${name}-select`}
          >
            {options.map((o) => (
              <MenuItem
                key={o.value}
                value={o.value}
                data-testid={`metadata-${name}-option-${o.value}`}
              >
                {o.label}
              </MenuItem>
            ))}
          </ClearableSelectWithPlaceholder>
        </FormControl>
      )}
    />
  );

  const renderAutocomplete = (
    name: keyof ClientMetadataFormValues,
    label: string,
    placeholder: string,
    options: { value: string; label: string }[],
  ) => (
    <Controller
      key={name}
      name={name}
      control={control}
      render={({ field }) => {
        const selectedOption =
          options.find((o) => o.value === field.value) ?? null;
        return (
          <FormControl>
            <Autocomplete
              options={options}
              value={selectedOption}
              onChange={(_, selectedValue) =>
                field.onChange(selectedValue?.value)
              }
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              disabled={!canEdit}
              data-testid={`metadata-${name}-autocomplete`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={placeholder}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  data-testid={`metadata-${name}-option-${field.value}`}
                />
              )}
            />
          </FormControl>
        );
      }}
    />
  );

  const { data: clientIntegrationTypesData } =
    CoreQuery.useAdmin_getSupportedClientIntegrationTypesQuery();
  const integrationTypes = useMemo(
    () => [
      ...(clientIntegrationTypesData?.integration_types ?? []).map((t) => ({
        value: t,
        label: t,
      })),
    ],
    [clientIntegrationTypesData?.integration_types],
  );

  const { data: stripePlatformsData } =
    CoreQuery.useAdmin_getStripePlatformsQuery();
  const stripePlatformOptions = useMemo(
    () => [
      ...(stripePlatformsData?.stripe_platforms ?? []).map((sp) => ({
        value: sp.id,
        label: sp.label,
      })),
    ],
    [stripePlatformsData?.stripe_platforms],
  );

  // `toDebtThreshold` returns the refund overdraft limit to be saved, in the
  // smallest currency unit. If the currency of the limit is unknown then the
  // field can't be edited, so the current limit is kept.
  const toDebtThreshold = (data: ClientMetadataFormValues) => {
    if (!debtThresholdCurrency) {
      return data.debt_threshold ?? null;
    }
    if (data.debt_threshold_major === "") {
      return null;
    }
    return parseMoney(data.debt_threshold_major, debtThresholdCurrency, locale);
  };

  const onSubmit = (data: ClientMetadataFormValues) => {
    const currentOnBehalfOf =
      clientData?.on_behalf_of_stripe_connected_account_id ?? "";
    const newOnBehalfOf = data.on_behalf_of_stripe_connected_account_id || "";
    const hasExistingFees = feesData?.fees && feesData.fees.length > 0;

    const isOnBehalfOfSet = currentOnBehalfOf === "" && newOnBehalfOf !== "";
    const isOnBehalfOfRemoved =
      currentOnBehalfOf !== "" && newOnBehalfOf === "";

    if (hasExistingFees && (isOnBehalfOfSet || isOnBehalfOfRemoved)) {
      if (isOnBehalfOfSet) {
        enqueueSnackbar(
          "Warning: OBO (On Behalf Of) has been enabled. Existing client fees may need to be updated to use the correct payment method configurations.",
          { variant: "warning" },
        );
      } else if (isOnBehalfOfRemoved) {
        enqueueSnackbar(
          "Warning: OBO (On Behalf Of) has been disabled. Existing client fees may need to be updated to use the correct payment method configurations.",
          { variant: "warning" },
        );
      }
    }

    updateMetadata(
      UpdateClientMetadataBody.fromJS({
        stripe_platform_id: data.stripe_platform_id || "",
        on_behalf_of_stripe_connected_account_id:
          data.on_behalf_of_stripe_connected_account_id || "",
        integration_type: data.integration_type || "",
        notes: data.notes || "",
        postmark_prefix: data.postmark_prefix || "",

        emails_disabled: data.emails_disabled,
        payment_reviews_enabled: data.payment_reviews_enabled,
        claiming_enabled: data.claiming_enabled,
        is_internal: data.is_internal,
        payment_capture_enabled: data.payment_capture_enabled,
        consolidated_payouts_enabled: data.consolidated_payouts_enabled,
        instant_payouts_enabled: data.instant_payouts_enabled,
        future_usage_payment_enabled: data.future_usage_payment_enabled,
        automatic_handover_confirmation_duration_seconds:
          data.automatic_handover_confirmation_duration_seconds,
        accept_deposit_allowance_seconds: data.accept_deposit_allowance_seconds,
        automatic_cancellation_enabled: data.automatic_cancellation_enabled,
        cover_extra_processing_cost: data.cover_extra_processing_cost,
        allocate_extra_price_to_client: data.allocate_extra_price_to_client,
        claim_email_matching_enabled: data.claim_email_matching_enabled,
        cover_postage_price_processing_cost:
          data.cover_postage_price_processing_cost,
        allocate_postage_price_to: data.allocate_postage_price_to,
        price_postage_bearer: data.price_postage_bearer,
        mcc:
          typeof data.mcc === "string" && data.mcc.length === 0
            ? null
            : data.mcc,
        product_description:
          typeof data.product_description === "string" &&
          data.product_description.length === 0
            ? null
            : data.product_description,
        website_url:
          typeof data.website_url === "string" && data.website_url.length === 0
            ? null
            : data.website_url,
        refund_after_funds_released_enabled:
          data.refund_after_funds_released_enabled,
        debt_threshold: toDebtThreshold(data),
        tax_reporting_eligible: data.tax_reporting_eligible,
        tax_report_type: data.tax_report_type,
        supplied_platform_operator_id: data.supplied_platform_operator_id || "",
      }),
    );

    updateFlags(
      UpdateClientFlagsBody.fromJS({
        show_account_balance: data.show_account_balance,
      }),
    );
  };

  const isLoading =
    isClientFetching ||
    isConnectedAccountFetching ||
    isClientFlagsFetching ||
    isMetadataUpdatePending ||
    isFlagsUpdatePending;
  const isDisabled = !isDirty;

  return (
    <Stack spacing={5} p={5}>
      <Typography variant="h5SolidDivider">
        <PublicSvgIcon name="build" /> Configuration
      </Typography>

      <Stack
        sx={{
          backgroundColor: "background.default",
          border: "1px solid",
          borderColor: "gray.200",
          borderRadius: 2,
          p: 3,
        }}
        flexDirection="column"
        spacing={3}
      >
        {renderToggle("emails_disabled", "Disable Transactional Emails", {
          tooltipTitle:
            "Stops Trustap transactional emails from being sent to this Client's users.",
        })}
        {renderToggle(
          "payment_reviews_enabled",
          "Pause Transaction if Payment is Flagged for Review",
          {
            tooltipTitle:
              "Payments flagged by Stripe Radar pause the transaction, which has to be manually reviewed to progress. This includes all new clients within the trial period.",
          },
        )}
        {renderToggle(
          "claiming_enabled",
          "Allow Client to Claim Guest Transactions",
          {
            tooltipTitle:
              "Allow the client to claim Guest transactions on behalf of the user.\n\nWarning: The client will have the ability to assign any transaction to any user. Use with caution.",
          },
        )}
        {renderToggle(
          "claim_email_matching_enabled",
          "Check emails match for claiming",
          {
            tooltipTitle:
              "When enabled, the guest user and full user claiming the transaction must be registered to the same email address. Note that this is only for the v2 API.",
          },
        )}
        {renderToggle("is_internal", "Retain Client ID on Redirect", {
          tooltipTitle:
            "Redirects to App retain original Client ID When users are redirected from a Client’s platform to the Trustap app to complete a transaction, the original Client ID is stored in the URL instead of being replaced with trustap-app and trustap-landing Client IDs.",
        })}
        {renderToggle(
          "payment_capture_enabled",
          "Authorize at Payment, Capture on Seller Acceptance",
          {
            tooltipTitle:
              "Availability of funds in the user’s account is authorized at payment, but funds are only captured after the seller accepts the transaction. This helps in reducing processing fees.",
          },
        )}
        {renderToggle("consolidated_payouts_enabled", "Consolidate Payouts", {
          tooltipTitle:
            "Combine multiple payouts into a single daily or weekly payout, reducing transaction fees charged by Stripe for each individual payout. Payout frequency can be configured in the Stripe dashboard. Can't be combined with Instant Payouts, so enabling this disables them.",
          onChange: (isEnabled) => {
            if (isEnabled) {
              setValue("instant_payouts_enabled", false, {
                shouldDirty: true,
              });
            }
          },
        })}
        {renderToggle("instant_payouts_enabled", "Enable Instant Payouts", {
          tooltipTitle:
            "Released funds arrive in the seller’s bank account within seconds, instead of the usual 1–7 days. Availability depends on the seller’s bank. An additional Stripe fee is deducted from the payout. Can't be combined with Consolidate Payouts, so enabling this disables it.",
          onChange: (isEnabled) => {
            if (isEnabled) {
              setValue("consolidated_payouts_enabled", false, {
                shouldDirty: true,
              });
            }
          },
        })}
        {isConsolidatedPayoutsEnabled && isInstantPayoutsEnabled && (
          <Alert severity="warning" data-testid="metadata-payouts-warning">
            Consolidate Payouts and Instant Payouts are both enabled, but only
            one of them can be. Disable one of them before saving.
          </Alert>
        )}
        {renderToggle(
          "future_usage_payment_enabled",
          "Enable Future Usage Payment",
          {
            tooltipTitle: "Enables Subscriptions and Post-Purchases",
          },
        )}
        {renderToggle(
          "refund_after_funds_released_enabled",
          "Enable refunds after funds released",
          {
            tooltipTitle: (
              <>
                <strong>
                  Warning: Enable only for single-seller clients without buyer
                  protection. Marketplaces are strictly unsupported.
                </strong>
                When enabled, refunds can be initiated after funds have already
                been paid out to the seller. If the seller’s account balance is
                insufficient to cover the refund, Trustap will advance the
                refund to the buyer from platform funds and log the amount owed
                by the seller. Payouts remain on hold until all refund costs are
                cleared. A bot runs hourly to check if the seller’s account
                balance is sufficient to clear any outstanding refunds costs in
                clean transfer reversals (partial clearing is unsupported).
                <br />
                <strong>Risk Warning:</strong> This exposes Trustap to bad debt
                risk and must be enabled with extreme caution.
              </>
            ),
            onChange: (isEnabled) => {
              if (
                isEnabled &&
                debtThresholdCurrency &&
                getValues("debt_threshold_major") === ""
              ) {
                setValue(
                  "debt_threshold_major",
                  formatMoneyInput(
                    clientMetadataConfig.defaultDebtThreshold,
                    debtThresholdCurrency,
                    locale,
                  ),
                  {
                    shouldDirty: true,
                  },
                );
              }
            },
          },
        )}
        {renderDebtThreshold()}
        {renderToggle(
          "show_account_balance",
          "Show Account Balance and Payout History",
          {
            tooltipTitle:
              "Allow access to the 'Account' tab, which shows the Payout History and Account Balance (if both the Stripe Platform ID and the OBO account ID are properly configured).",
          },
        )}
        {renderToggle(
          "automatic_cancellation_enabled",
          "Enable Automatic Cancellation",
          {
            tooltipTitle:
              "Enable created and joined transactions to be automatically cancelled by the bot after 7 minutes if not paid (enabled by default).",
          },
        )}
        {renderToggle(
          "cover_extra_processing_cost",
          "Cover Extra Processing Cost",
          {
            tooltipTitle:
              "When enabled, the client covers any extra processing costs instead of passing them to the buyer.",
          },
        )}
        {renderToggle(
          "allocate_extra_price_to_client",
          "Allocate Extra Price to Client",
          {
            tooltipTitle:
              "When enabled, any extra price is allocated to the client rather than the seller.",
          },
        )}
        {renderToggle(
          "cover_postage_price_processing_cost",
          "Cover Postage Price Processing Cost",
          {
            tooltipTitle:
              "When enabled, the client covers the processing cost for postage price instead of passing it to the buyer.",
          },
        )}
        {renderSelect(
          "allocate_postage_price_to",
          "Allocate Postage Price To",
          "Choose recipient",
          [
            { value: "seller", label: "Seller" },
            { value: "client", label: "Client" },
            { value: "service", label: "Service" },
          ],
          { size: "small" },
        )}
        {renderSelect(
          "price_postage_bearer",
          "Postage Price Bearer",
          "Choose bearer",
          [
            { value: "buyer", label: "Buyer" },
            { value: "seller", label: "Seller" },
            { value: "client", label: "Client" },
          ],
          { size: "small" },
        )}
        {renderToggle(
          "tax_reporting_eligible",
          "Enable Tax Compliance Tracking",
          {
            tooltipTitle:
              "When enabled, this client's sellers are tracked against tax reporting thresholds. Once a seller crosses the threshold, they receive completeness reminders and, if unresolved, have their transactions and then payouts blocked. A Supplied Platform Operator ID must be set before this can be enabled.",
          },
        )}
        {renderSelect(
          "tax_report_type",
          "Tax Report Type",
          "Choose report type",
          [
            { value: "dac7", label: "DAC7" },
            { value: "drr", label: "DRR" },
          ],
          {
            size: "small",
            disabled: !isTaxReportingEligible,
            isClearable: false,
          },
        )}
      </Stack>

      <Typography variant="body1DashedDivider">Integration Type</Typography>
      {renderSelect(
        "integration_type",
        "Integration type",
        "Choose Integration type",
        integrationTypes,
      )}

      <Typography variant="body1DashedDivider">Stripe Configuration</Typography>
      {renderSelect(
        "stripe_platform_id",
        "Stripe Platform ID",
        "Choose Platform",
        stripePlatformOptions,
      )}
      {renderText(
        "on_behalf_of_stripe_connected_account_id",
        "On behalf of Stripe connected account ID",
        "Enter an Account ID",
      )}

      <Typography variant="body1DashedDivider">
        Supplied Configuration
      </Typography>
      {renderSuppliedPlatformOperatorID()}

      <Typography variant="body1DashedDivider">Email Configuration</Typography>
      {renderText(
        "postmark_prefix",
        "Postmark prefix used for email address",
        "Enter a Prefix",
      )}

      <Typography variant="body1DashedDivider">
        Seller handover confirmation&nbsp;
        <Tooltip title="If set, automatically confirms the handover on behalf of the seller.">
          <PublicSvgIcon name="info" />
        </Tooltip>
      </Typography>
      <DaysHoursFields
        fieldName="automatic_handover_confirmation_duration_seconds"
        control={control}
        dataTestIdPrefix="metadata-automatic-handover"
        disabled={!canEdit}
      />

      <Typography variant="body1DashedDivider">
        Accept deposit allowance&nbsp;
        <Tooltip title="If set, automatically cancels transactions where the payment has not been accepted for the allowed period">
          <PublicSvgIcon name="info" />
        </Tooltip>
      </Typography>
      <DaysHoursFields
        fieldName="accept_deposit_allowance_seconds"
        control={control}
        dataTestIdPrefix="metadata-accept-deposit"
        disabled={!canEdit}
      />

      <Typography variant="body1DashedDivider">Notes</Typography>
      {renderText("notes", "Add Notes here", "Enter a Note", {
        rows: 4,
      })}

      <Typography variant="body1DashedDivider">
        Connected account business profile&nbsp;
        <Tooltip title="KYC prefill values used to populate an individual's connected account business profile">
          <PublicSvgIcon name="info" />
        </Tooltip>
      </Typography>
      {renderAutocomplete(
        "mcc",
        "Merchant Category Code",
        "Select a Merchant Category Code",
        mccOptions,
      )}

      {renderText(
        "product_description",
        "Product Description",
        "Enter Product Description",
      )}

      {renderText("website_url", "Website URL", "Enter Website URL")}

      {canEdit && (
        <Button
          variant="contained"
          disabled={isDisabled}
          loading={isLoading}
          onClick={handleSubmit(onSubmit)}
          size="large"
        >
          Save Metadata
        </Button>
      )}
    </Stack>
  );
}
